import {
    useCallback, useMemo, useRef, useEffect,
} from 'react'
import omit from 'lodash/omit'
import noop from 'lodash/noop'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import Button from 'app/shared-components/Button'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import WarningCard from 'app/shared-components/WarningCard'

import StatusHandler from 'app/shared-components/StatusHandler'

import useGetLaneFee from 'app/Apps/Pricing/LaneGroups/LaneFees/hooks/useGetLaneFee'
import {
    useCollapsibleTableContext,
} from 'app/shared-components/CollapsibleTable'
import LaneGroupRequests from 'app/requests/ContractBasis/LaneGroup/LaneGroup.requests'

import LaneFeesForm from '../LaneFeesForm'
import LaneFeesSetStatusButtons from '../LaneFeesSetStatusButtons'
import prepareDataForCreation from './LaneFeesCopy.utils'

export default function LaneFeesCopy({
    id,
    refetch = noop,
}: Readonly<{ id: number, refetch?: (...args: any[]) => void }>) {
    const {
        updatePathWithParams,
    } = useCollapsibleTableContext()

    const ref = useRef(null)

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetLaneFee(id)

    const onSuccess = useCallback(({
        id: newId,
    }) => {
        updatePathWithParams({
            id: newId,
        })
        refetch()
    }, [
        updatePathWithParams,
        refetch,
    ])

    const onCancel = useCallback(() => {
        updatePathWithParams({
            id: data?.id,
        })
    }, [
        data?.id,
        updatePathWithParams,
    ])

    const copyData = useMemo(() => {
        return {
            ...omit(data, [
                'id',
                'changedBy',
                'changedOn',
                'laneFeesNumber',
                'laneFeesStatus',
            ]),
            originId: data?.id,
        }
    }, [data])

    const {
        mutate: create,
        isPending,
    } = useSimpleApiRequest({
        onSuccess,
        name: 'LaneFeesCopy',
        requestParams: LaneGroupRequests.createLaneFee({
            laneGroupId: copyData?.laneGroupId,
        }),
        title: 'Lane Fees Copy Created',
    })

    const getActionButtonsPanel = useCallback(({
        value, isValid,
    }) => {
        return (
            <ButtonContainer>
                <LaneFeesSetStatusButtons
                    key="set-status-buttons"
                    value={value}
                    onCreate={(newValue) => {
                        create(prepareDataForCreation(newValue))
                    }}
                    disabled={!isValid}
                    isLoading={isPending}
                />
                <Button
                    disabled={isPending}
                    secondary
                    label="Cancel"
                    onClick={onCancel}
                />
            </ButtonContainer>
        )
    }, [
        create,
        isPending,
        onCancel,
    ])

    useEffect(() => {
        if (isDataReady && ref.current) {
            ref.current.scrollIntoView({
                behavior: 'smooth',
            })
        }
    }, [isDataReady])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <div ref={ref}>
                    <WarningCard>
                        You are copying a Lane Fee.
                    </WarningCard>
                </div>
                <LaneFeesForm
                    data={copyData}
                    getActionButtonsPanel={getActionButtonsPanel}
                />
            </>
        </StatusHandler>
    )
}
